// AccountPage.jsx — FIXED
// Fixes applied:
//   ✅ 405 on brand-profile GET → uses axios.get with correct param, falls back gracefully
//   ✅ No infinite loop → email read once via useRef, stable deps
//   ✅ No duplicate usage fetches → fetched once in main, passed down as props
//   ✅ API prefix deduplication → API = VITE_API_URL + "/api", all fetch calls use it cleanly
//   ✅ zenithpostpost branding
//   ✅ All 5 tabs: Usage, Brand Profile, Account, Subscription, Top-ups
//   ✅ Brand profile sections: view + inline edit with Other flows, platform tones, color pickers
//   ✅ Subscription: status chip, cancel (immediate UI update), billing portal, resubscribe
//   ✅ Top-ups: type select, qty select, Stripe redirect
//   ✅ Auth guard on mount

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { SketchPicker } from "react-color";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Checkbox,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  Collapse,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Divider,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SaveIcon from "@mui/icons-material/Save";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/api";
import { getEmailFromToken } from "./auth";


// ─── MUI Theme ───────────────────────────────────────────────────────────────
const muiTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#6c7ef8" },
    secondary: { main: "#34d399" },
    background: { default: "#111318", paper: "#191c24" },
    text: { primary: "#f0f2f7", secondary: "#b0b7ce" },
  },
  typography: { fontFamily: "'DM Sans', system-ui, sans-serif" },
});
const email = getEmailFromToken();

const D = {
  bg: "#111318",
  surface: "#191c24",
  surfaceUp: "#1f2230",
  border: "rgba(255,255,255,0.07)",
  borderMed: "rgba(255,255,255,0.11)",
  accent: "#6c7ef8",
  accentSoft: "rgba(108,126,248,0.1)",
  accentBorder: "rgba(108,126,248,0.2)",
  success: "#34d399",
  successSoft: "rgba(52,211,153,0.1)",
  warn: "#f59e0b",
  warnSoft: "rgba(245,158,11,0.08)",
  danger: "#f87171",
  dangerSoft: "rgba(248,113,113,0.1)",
  purple: "#a37bf8",
  textPrimary: "#f0f2f7",
  textSec: "#b0b7ce",
  textMuted: "#6b7390",
  fontDisplay: "'Plus Jakarta Sans', 'DM Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'JetBrains Mono', monospace",
};

const G = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  body { background:#111318; color:#f0f2f7; font-family:'DM Sans',sans-serif; -webkit-font-smoothing:antialiased; }
  ::selection { background:rgba(108,126,248,0.25); }
  ::-webkit-scrollbar { width:5px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.1); border-radius:99px; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
  @keyframes spin { to{transform:rotate(360deg)} }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
  @keyframes logoGlow { 0%,100%{box-shadow:0 0 16px rgba(108,126,248,0.3)} 50%{box-shadow:0 0 28px rgba(163,123,248,0.45)} }
  .fade-up   { animation: fadeUp 0.45s cubic-bezier(0.22,1,0.36,1) both; }
  .fade-up-1 { animation-delay:0.07s; }
  .fade-up-2 { animation-delay:0.14s; }
  .page-glow { position:fixed;inset:0;z-index:0;pointer-events:none;background:radial-gradient(ellipse 60% 40% at 10% 0%,rgba(108,126,248,0.04) 0%,transparent 65%),radial-gradient(ellipse 40% 35% at 90% 100%,rgba(52,211,153,0.03) 0%,transparent 65%); }
  .MuiOutlinedInput-root { border-radius:10px !important; background:rgba(255,255,255,0.03) !important; font-family:'DM Sans',sans-serif !important; font-size:14px !important; }
  .MuiOutlinedInput-notchedOutline { border-color:rgba(255,255,255,0.09) !important; }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color:rgba(255,255,255,0.18) !important; }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color:#6c7ef8 !important; border-width:1.5px !important; }
  .MuiOutlinedInput-root.Mui-focused { box-shadow:0 0 0 3px rgba(108,126,248,0.12) !important; }
  .MuiFormHelperText-root { font-family:'JetBrains Mono',monospace !important; font-size:10.5px !important; color:#6b7390 !important; }
  .MuiInputLabel-root { font-family:'DM Sans',sans-serif !important; color:#8b91a8 !important; }
  .MuiInputLabel-root.Mui-focused { color:#6c7ef8 !important; }
  .MuiMenuItem-root { font-family:'DM Sans',sans-serif !important; font-size:13.5px !important; color:#d0d5e8 !important; }
  .MuiMenuItem-root:hover { background:rgba(108,126,248,0.08) !important; }
  .MuiMenuItem-root.Mui-selected { background:rgba(108,126,248,0.12) !important; }
  .MuiChip-root { font-family:'DM Sans',sans-serif !important; font-size:12px !important; font-weight:600 !important; border-radius:6px !important; }
  .MuiButton-root { border-radius:9px !important; text-transform:none !important; font-family:'DM Sans',sans-serif !important; font-weight:600 !important; }
  .MuiTab-root { text-transform:none !important; font-family:'DM Sans',sans-serif !important; font-weight:500 !important; font-size:13.5px !important; min-height:44px !important; color:#b0b7ce !important; }
  .MuiTab-root.Mui-selected { color:#f0f2f7 !important; font-weight:600 !important; }
  .MuiTabs-indicator { background:#6c7ef8 !important; height:2px !important; border-radius:2px !important; }
  .MuiLinearProgress-root { border-radius:99px !important; background:rgba(255,255,255,0.06) !important; height:5px !important; }
  .MuiLinearProgress-bar { border-radius:99px !important; background:#6c7ef8 !important; }
  .MuiRadio-root.Mui-checked { color:#6c7ef8 !important; }
  .MuiCheckbox-root.Mui-checked svg { color:#6c7ef8 !important; }
  .MuiDivider-root { border-color:rgba(255,255,255,0.07) !important; }
  .MuiTooltip-tooltip { background:#1f2230 !important; border:1px solid rgba(255,255,255,0.1) !important; border-radius:8px !important; font-family:'DM Sans',sans-serif !important; font-size:12px !important; color:#f0f2f7 !important; padding:6px 10px !important; }
  .MuiTooltip-arrow { color:#1f2230 !important; }
  .MuiFormControlLabel-label { font-size:13.5px !important; color:#b0b7ce !important; }
  .prof-section { background:#191c24; border:1px solid rgba(255,255,255,0.07); border-radius:14px; margin-bottom:10px; overflow:hidden; transition:border-color 0.2s; }
  .prof-section.editing { border-color:rgba(108,126,248,0.25); }
  .prof-section-head { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; cursor:pointer; transition:background 0.15s; }
  .prof-section-head:hover { background:rgba(255,255,255,0.015); }
  .prof-section-body { padding:0 22px 22px; }
  .f-label { font-family:'JetBrains Mono',monospace; font-size:10.5px; font-weight:600; color:#6b7390; text-transform:uppercase; letter-spacing:0.07em; margin-bottom:7px; display:flex; align-items:center; gap:6px; }
  .f-val { font-size:14px; color:#c0c7de; line-height:1.65; padding:10px 14px; border-radius:9px; background:rgba(255,255,255,0.025); border:1px solid rgba(255,255,255,0.06); word-break:break-word; min-height:40px; }
  .f-val.empty { color:#6b7390; font-style:italic; }
  .edit-bar { display:flex; align-items:center; justify-content:space-between; padding:11px 22px; background:rgba(108,126,248,0.06); border-bottom:1px solid rgba(108,126,248,0.15); }
  .metric-card { background:#191c24; border:1px solid rgba(255,255,255,0.07); border-radius:14px; padding:24px; transition:border-color 0.2s,transform 0.2s; }
  .metric-card:hover { border-color:rgba(255,255,255,0.14); transform:translateY(-1px); }
  .color-swatch { display:inline-flex; align-items:center; gap:8px; padding:7px 12px; border-radius:8px; border:1px solid rgba(255,255,255,0.08); background:rgba(255,255,255,0.025); }
  .sk { background:rgba(255,255,255,0.05); background-image:linear-gradient(90deg,rgba(255,255,255,0.04) 0px,rgba(255,255,255,0.08) 300px,rgba(255,255,255,0.04) 600px); background-size:600px; animation:shimmer 1.6s ease infinite; border-radius:8px; }
  .spin { display:inline-block; width:13px; height:13px; border-radius:50%; border:2px solid rgba(108,126,248,0.25); border-top-color:#6c7ef8; animation:spin 0.7s linear infinite; flex-shrink:0; }
  .optional-accordion { border:1.5px dashed rgba(255,255,255,0.1); border-radius:12px; overflow:hidden; margin-bottom:0; }
  .optional-accordion-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; cursor:pointer; background:rgba(255,255,255,0.02); transition:background 0.15s; }
  .optional-accordion-head:hover { background:rgba(108,126,248,0.07); }
  .topup-card { padding:18px 20px; border-radius:12px; border:1px solid rgba(255,255,255,0.07); background:#1f2230; transition:border-color 0.18s,transform 0.18s; cursor:pointer; }
  .topup-card:hover { border-color:rgba(108,126,248,0.2); transform:translateY(-1px); }
  .topup-card.selected { border-color:#6c7ef8; background:rgba(108,126,248,0.1); }
  .status-chip { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:8px; font-family:'JetBrains Mono',monospace; font-size:11px; font-weight:700; letter-spacing:0.06em; }
  .status-chip.active   { background:rgba(52,211,153,0.1);  border:1px solid rgba(52,211,153,0.3);  color:#34d399; }
  .status-chip.canceled { background:rgba(245,158,11,0.1);  border:1px solid rgba(245,158,11,0.3);  color:#f59e0b; }
  .status-chip.expired  { background:rgba(248,113,113,0.1); border:1px solid rgba(248,113,113,0.3); color:#f87171; }
  .status-chip.none     { background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.12);color:#6b7390; }
  @media (max-width: 768px) {
    .MuiTabs-root { max-width: 100vw; }
    .MuiTab-root { font-size: 12px !important; padding: 6px 10px !important; min-width: unset !important; }
  }
  @media (max-width: 480px) {
    .MuiTab-root { font-size: 11px !important; padding: 4px 8px !important; }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// OPTIONS ARRAYS
// ─────────────────────────────────────────────────────────────────────────────
const industriesOptions = [
  "Pet Care",
  "Healthcare",
  "AI / Technology",
  "Consumer Wellness",
  "E-commerce",
  "Education",
  "Finance",
  "Media",
  "Retail",
  "Other",
  "Food & Beverage",
  "Travel & Tourism",
  "Automotive",
  "Fashion & Apparel",
  "Fitness & Sports",
  "Beauty & Cosmetics",
  "Home & Garden",
  "Telecommunications",
  "Software / SaaS",
  "Entertainment",
  "Gaming",
  "Real Estate",
  "Logistics & Transportation",
  "Non-profit / NGOs",
  "Hospitality",
  "Pharmaceuticals",
  "Insurance",
  "Legal Services",
  "Marketing & Advertising",
  "Energy & Utilities",
  "Manufacturing",
  "Publishing",
  "Event Management",
  "Consumer Electronics",
];
const targetAudienceOptions = [
  "First-time customers",
  "Existing customers",
  "Families",
  "Professionals",
  "Enthusiasts / Hobbyists",
  "Emotionally driven buyers",
  "Data-driven buyers",
  "B2B Clients",
  "Other",
  "Millennials",
  "Gen Z",
  "Baby Boomers",
  "High-income earners",
  "Middle-income earners",
  "Students",
  "Entrepreneurs / Startups",
  "Small business owners",
  "Corporate executives",
  "Frequent travelers",
  "Eco-conscious buyers",
  "Tech-savvy users",
  "Health-conscious users",
  "Luxury consumers",
  "Budget-conscious consumers",
  "Parents",
  "Pet owners",
  "Urban residents",
  "Suburban residents",
  "Remote workers",
  "Creatives / Artists",
  "Investors / Traders",
  "Community organizers",
  "Pet Owners",
];
const brandValuesOptions = [
  "Trust",
  "Empathy",
  "Innovation",
  "Transparency",
  "Simplicity",
  "Authority",
  "Care",
  "Creativity",
  "Sustainability",
  "Other",
  "Responsibility",
  "Authenticity",
  "Collaboration",
  "Diversity & Inclusion",
  "Excellence",
  "Courage",
  "Reliability",
  "Passion",
  "Commitment",
  "Customer-centricity",
  "Flexibility",
  "Accountability",
  "Fun",
  "Innovation-driven",
  "Quality",
  "Growth",
  "Leadership",
  "Integrity",
  "Adventure",
  "Inspiration",
];
const brandPersonalityOptions = [
  "Warm",
  "Playful",
  "Calm",
  "Professional",
  "Bold",
  "Reflective",
  "Friendly",
  "Curious",
  "Adventurous",
  "Innovative",
  "Other",
  "Energetic",
  "Charismatic",
  "Sophisticated",
  "Reliable",
  "Passionate",
  "Creative",
  "Humorous",
  "Inclusive",
  "Optimistic",
  "Confident",
  "Patient",
  "Grounded",
  "Dynamic",
  "Authentic",
  "Empathetic",
  "Visionary",
  "Inspirational",
  "Strategic",
  "Approachable",
];
const brandArchetypeOptions = [
  "The Caregiver",
  "The Guide",
  "The Hero",
  "The Creator",
  "The Explorer",
  "The Lover",
  "The Jester",
  "The Sage",
  "The Innocent",
  "Other",
  "The Ruler",
  "The Magician",
  "The Everyman",
  "The Rebel",
  "The Visionary",
  "The Innovator",
  "The Protector",
  "The Connector",
  "The Pioneer",
  "The Advocate",
  "The Storyteller",
  "The Strategist",
  "The Builder",
  "The Mentor",
  "The Entertainer",
  "The Designer",
  "The Philosopher",
  "The Champion",
  "The Leader",
  "The Altruist",
];
const writingVoiceOptions = [
  "Friendly guide",
  "Experienced mentor",
  "Calm authority",
  "Curious storyteller",
  "Professional expert",
  "Conversational",
  "Inspirational",
  "Authoritative",
  "Other",
  "Humorous",
  "Empathetic",
  "Persuasive",
  "Educational",
  "Engaging",
  "Concise",
  "Formal",
  "Informal",
  "Narrative",
  "Technical",
  "Analytical",
  "Motivational",
  "Encouraging",
  "Inclusive",
  "Thought-provoking",
  "Direct",
  "Reflective",
  "Optimistic",
  "Story-driven",
  "Visionary",
];
const avoidContentOptions = [
  "Fear-based language",
  "Aggressive sales tactics",
  "Hype / exaggeration",
  "Medical or legal guarantees",
  "False urgency",
  "Clickbait headlines",
  "Misleading claims",
  "Negative competitor comparisons",
  "Political statements",
  "Religious messaging",
  "Controversial social issues",
  "Profanity",
  "Slang or overly casual tone",
  "Corporate jargon",
  "Buzzwords without substance",
  "Overly technical language",
  "Complex legal disclaimers",
  "Hard-selling CTAs",
  "Emotional manipulation",
  "Scarcity pressure tactics",
  "Guilt-based messaging",
  "Body shaming language",
  "Stereotyping",
  "Cultural insensitivity",
  "Gender bias",
  "Unrealistic results",
  "Fake testimonials",
  "Overpromising outcomes",
  "Humor about sensitive topics",
  "Excessive emojis",
  "Other",
];
const visualStyleOptions = [
  "Soft daylight",
  "Cinematic",
  "Editorial photography",
  "Minimalist",
  "Lifestyle",
  "Studio-style",
  "Other",
  "Vintage",
  "Rustic",
  "Bold colors",
  "Monochrome",
  "High contrast",
  "Flat design",
  "Illustrative",
  "3D renders",
  "Abstract",
  "Playful",
  "Luxury / Premium",
  "Muted tones",
  "Nature-inspired",
  "Geometric",
  "Textured",
  "Hand-drawn",
  "Animated",
  "Collage",
  "Surreal",
  "Pop art",
  "Clean & modern",
  "Futuristic",
  "Artistic",
];
const positioningStyleOptions = [
  "Premium",
  "Friendly & Accessible",
  "Science-backed",
  "Lifestyle-oriented",
  "Other",
  "Affordable",
  "Luxury",
  "Innovative",
  "Trustworthy",
  "Convenient",
  "Exclusive",
  "Eco-friendly",
  "Community-focused",
  "Data-driven",
  "Fast & Reliable",
  "Expert-driven",
  "Customer-first",
  "Emotional appeal",
  "Practical",
  "Authentic",
  "High-tech",
  "Cutting-edge",
  "Minimalist",
  "Versatile",
  "Comfortable",
  "Playful",
  "Youthful",
  "Professional",
  "Inspirational",
  "Strategic",
];
const platformToneOptions = {
  instagram: [
    "Emotional & intimate",
    "Light & playful",
    "Calm & reflective",
    "Trendy",
    "Inspirational",
    "Vibrant & colorful",
    "Authentic",
    "Humorous",
    "Minimalist",
    "Luxury-focused",
    "Casual",
    "Artistic",
    "Youthful",
    "Story-driven",
    "Bold & daring",
    "Community-focused",
    "Emotional storytelling",
    "Motivational",
    "Adventure-focused",
    "Creative visuals",
    "Whimsical",
    "Informative",
    "Heartfelt",
    "Engaging",
    "Visionary",
    "Professional",
    "Cinematic",
    "Tech-focused",
    "Eco-conscious",
    "Inclusive",
    "Other",
  ],
  facebook: [
    "Community-focused",
    "Story-driven",
    "Informative",
    "Engaging",
    "Inspirational",
    "Friendly & supportive",
    "Educational",
    "Casual",
    "Authoritative",
    "Professional",
    "Motivational",
    "Humorous",
    "Emotional",
    "Data-driven",
    "Reliable",
    "Event-focused",
    "Conversational",
    "Inclusive",
    "Trustworthy",
    "Creative",
    "Optimistic",
    "Helpful",
    "Visionary",
    "Local-focused",
    "Lifestyle-oriented",
    "Entertainment-driven",
    "Interactive",
    "News-style",
    "Socially conscious",
    "Customer-centric",
    "Other",
  ],
  linkedIn: [
    "Thought leadership",
    "Insight-driven",
    "Educational",
    "Professional",
    "Authoritative",
    "Expert advice",
    "Data-driven",
    "Analytical",
    "Career-focused",
    "Networking-focused",
    "Engaging",
    "Motivational",
    "Strategic",
    "Innovative",
    "Trustworthy",
    "Informative",
    "Executive-focused",
    "B2B-focused",
    "Industry insights",
    "Leadership",
    "Research-based",
    "Problem-solving",
    "Analytical storytelling",
    "Results-driven",
    "Visionary",
    "Professional guidance",
    "Expert commentary",
    "Strategic insights",
    "Ethical",
    "Inclusive",
    "Other",
  ],
  newsletter: [
    "Conversational",
    "Calm narration",
    "Documentary-style",
    "Storytelling",
    "Dynamic & engaging",
    "Humorous",
    "Emotional",
    "Inspirational",
    "Educational",
    "Professional",
    "Informative",
    "Playful",
    "Energetic",
    "Authentic",
    "Minimalist",
    "Cinematic",
    "Luxury feel",
    "Casual",
    "Heartfelt",
    "Action-oriented",
    "Adventure-focused",
    "Tech-driven",
    "Creative visuals",
    "Community-focused",
    "Motivational",
    "Interactive",
    "Empowering",
    "Data-driven",
    "Thought-provoking",
    "Relaxed & friendly",
    "Other",
  ],
  twitter: [
    "Conversational",
    "Calm narration",
    "Documentary-style",
    "Storytelling",
    "Dynamic & engaging",
    "Humorous",
    "Emotional",
    "Inspirational",
    "Educational",
    "Professional",
    "Informative",
    "Playful",
    "Energetic",
    "Authentic",
    "Minimalist",
    "Cinematic",
    "Luxury feel",
    "Casual",
    "Heartfelt",
    "Action-oriented",
    "Adventure-focused",
    "Tech-driven",
    "Creative visuals",
    "Community-focused",
    "Motivational",
    "Interactive",
    "Empowering",
    "Data-driven",
    "Thought-provoking",
    "Relaxed & friendly",
    "Other",
  ],
  blog: [
    "Conversational",
    "Calm narration",
    "Documentary-style",
    "Storytelling",
    "Dynamic & engaging",
    "Humorous",
    "Emotional",
    "Inspirational",
    "Educational",
    "Professional",
    "Informative",
    "Playful",
    "Energetic",
    "Authentic",
    "Minimalist",
    "Cinematic",
    "Luxury feel",
    "Casual",
    "Heartfelt",
    "Action-oriented",
    "Adventure-focused",
    "Tech-driven",
    "Creative visuals",
    "Community-focused",
    "Motivational",
    "Interactive",
    "Empowering",
    "Data-driven",
    "Thought-provoking",
    "Relaxed & friendly",
    "Other",
  ],
};
const emotionalThemeOptions = [
  "Trust",
  "Reassurance",
  "Growth",
  "Curiosity",
  "Confidence",
  "Happiness",
  "Excitement",
  "Empathy",
  "Hope",
  "Inspiration",
  "Motivation",
  "Joy",
  "Security",
  "Adventure",
  "Luxury",
  "Professionalism",
  "Innovation",
  "Creativity",
  "Authenticity",
  "Passion",
  "Community",
  "Courage",
  "Optimism",
  "Comfort",
  "Serenity",
  "Wisdom",
  "Fun",
  "Love",
  "Respect",
  "Empowerment",
];
const seasonalContextOptions = [
  "Evergreen",
  "Holiday",
  "Back-to-work",
  "Product launch",
  "Black Friday / Cyber Monday",
  "Christmas",
  "New Year",
  "Valentine's Day",
  "Easter",
  "Summer Sale",
  "Back-to-school",
  "Spring Sale",
  "Autumn Sale",
  "Halloween",
  "Mother's Day",
  "Father's Day",
  "Independence Day",
  "Thanksgiving",
  "Graduation",
  "Seasonal Campaign",
  "Anniversary",
  "Flash Sale",
  "Limited Edition",
  "Weekly Special",
  "Winter Sale",
  "Quarterly Promotion",
  "End-of-season",
  "Special Event",
  "Charity Campaign",
  "Awareness Week",
];
const funnelStageOptions = [
  "Mostly Cold — never heard of us",
  "Mostly Warm — aware but not committed",
  "Mostly Hot — ready to buy",
  "Mixed — roughly equal cold & warm",
  "Mixed — roughly equal warm & hot",
  "Mixed — all three stages",
];
const COLOR_KEYS = ["primary", "secondary", "background", "accent"];

const PLATFORM_META = {
  instagram: ["📸", "Instagram"],
  facebook: ["📘", "Facebook"],
  linkedIn: ["💼", "LinkedIn"],
  twitter: ["𝕏", "Twitter"],
  newsletter: ["📬", "Newsletter"],
  blog: ["📝", "Blog"],
};

const PROFILE_SECTIONS = [
  {
    id: "basics",
    icon: "🏢",
    title: "Company Basics",
    subtitle: "Brand name, website, industry & audience",
    fields: [
      "brandName",
      "companyWebsite",
      "contentLanguage",
      "industries",
      "targetAudiences",
    ],
  },
  {
    id: "identity",
    icon: "🎯",
    title: "Mission & Identity",
    subtitle: "What you stand for and who you are",
    fields: [
      "brandMission",
      "brandArchetype",
      "brandValues",
      "brandPersonality",
    ],
  },
  {
    id: "voice",
    icon: "🎙",
    title: "Voice & Tone",
    subtitle: "How your brand speaks across every channel",
    fields: [
      "writingVoice",
      "avoidContent",
      "emotionalToneRange",
      "sentenceComplexity",
    ],
  },
  {
    id: "products",
    icon: "📦",
    title: "Products & Positioning",
    subtitle: "What you sell and where you stand in market",
    fields: [
      "services",
      "valueProposition",
      "positioningStyle",
      "problemSolved",
      "competitors",
      "competitiveAdvantage",
    ],
  },
  {
    id: "visual",
    icon: "🎨",
    title: "Visual Identity",
    subtitle: "Colors, style, and brand visuals",
    fields: ["visualStyle", "colorPalette", "logoPlacement"],
  },
  {
    id: "platforms",
    icon: "📱",
    title: "Platform Preferences",
    subtitle: "Channel-specific tone settings",
    fields: ["platformPreferences"],
  },
  {
    id: "campaign",
    icon: "📣",
    title: "Campaign Context",
    subtitle: "Active campaign settings (optional)",
    fields: ["campaignContext"],
  },
  {
    id: "strategy",
    icon: "🧠",
    title: "Brand Strategy & AI Intelligence",
    subtitle: "Deep strategy powering your content agents",
    fields: [
      "brandPromise",
      "brandEnemy",
      "coreBeliefs",
      "primaryCtaPerPlatform",
      "claimsNotAllowed",
      "voiceReferenceExamples",
      "exactAudienceLanguage",
      "signaturePhrases",
      "audienceFears",
      "customerTransformationStory",
      "socialProofData",
      "funnelStageFocus",
      "funnelStageBreakdown",
    ],
  },
];

const REQUIRED_KEYS = [
  "brandName",
  "companyWebsite",
  "contentLanguage",
  "industries",
  "targetAudiences",
  "brandValues",
  "brandPersonality",
  "brandArchetype",
  "writingVoice",
  "avoidContent",
  "visualStyle",
  "positioningStyle",
  "services",
  "brandMission",
  "valueProposition",
  "problemSolved",
  "competitors",
  "competitiveAdvantage",
  "brandPromise",
  "brandEnemy",
  "coreBeliefs",
];

const FIELD_LABELS = {
  brandName: "Brand Name",
  companyWebsite: "Website",
  contentLanguage: "Content Language",
  industries: "Industries",
  targetAudiences: "Target Audiences",
  brandMission: "Brand Mission",
  brandArchetype: "Brand Archetype",
  brandValues: "Brand Values",
  brandPersonality: "Brand Personality",
  writingVoice: "Writing Voice",
  avoidContent: "Avoid in Content",
  emotionalToneRange: "Emotional Tone",
  sentenceComplexity: "Sentence Complexity",
  services: "Products / Services",
  valueProposition: "Value Proposition",
  positioningStyle: "Positioning Style",
  problemSolved: "Problem Solved",
  competitors: "Competitors",
  competitiveAdvantage: "Competitive Advantage",
  visualStyle: "Visual Style",
  colorPalette: "Brand Colors",
  logoPlacement: "Logo Placement",
  platformPreferences: "Platform Tones",
  campaignContext: "Campaign Context",
  brandPromise: "Brand Promise (Q01)",
  brandEnemy: "Brand Enemy (Q02)",
  coreBeliefs: "Core Beliefs (Q03)",
  primaryCtaPerPlatform: "Primary CTA (Q10)",
  claimsNotAllowed: "Hard Guardrails (Q12)",
  voiceReferenceExamples: "Voice References (Q04)",
  exactAudienceLanguage: "Audience Language (Q05)",
  signaturePhrases: "Signature Phrases (Q06)",
  audienceFears: "Audience Fears (Q07)",
  customerTransformationStory: "Transformation Story (Q08)",
  socialProofData: "Social Proof (Q09)",
  funnelStageFocus: "Funnel Stage (Q11)",
  funnelStageBreakdown: "Funnel % Breakdown",
};

const OPTIONAL_FIELDS = new Set([
  "voiceReferenceExamples",
  "exactAudienceLanguage",
  "signaturePhrases",
  "audienceFears",
  "customerTransformationStory",
  "socialProofData",
  "funnelStageFocus",
  "funnelStageBreakdown",
  "campaignContext",
  "logoPlacement",
]);
const TEXTAREA_FIELDS = new Set([
  "brandMission",
  "valueProposition",
  "problemSolved",
  "competitors",
  "competitiveAdvantage",
  "brandPromise",
  "brandEnemy",
  "coreBeliefs",
  "primaryCtaPerPlatform",
  "claimsNotAllowed",
  "voiceReferenceExamples",
  "exactAudienceLanguage",
  "signaturePhrases",
  "audienceFears",
  "customerTransformationStory",
  "socialProofData",
  "funnelStageBreakdown",
]);

const MULTI_FIELDS = {
  industries: industriesOptions,
  targetAudiences: targetAudienceOptions,
  brandValues: brandValuesOptions,
  brandPersonality: brandPersonalityOptions,
  brandArchetype: brandArchetypeOptions,
  writingVoice: writingVoiceOptions,
  avoidContent: avoidContentOptions,
  visualStyle: visualStyleOptions,
  positioningStyle: positioningStyleOptions,
};

const OTHER_FIELDS = {
  industries: "otherIndustry",
  targetAudiences: "otherTargetAudience",
  brandValues: "otherBrandValue",
  brandPersonality: "otherBrandPersonality",
  brandArchetype: "otherBrandArchetype",
  writingVoice: "otherWritingVoice",
  avoidContent: "otherAvoidContent",
  visualStyle: "otherVisualStyle",
  positioningStyle: "otherPositioningStyle",
};

// Demo fallback profile
const DEMO_PROFILE = {
  brandName: "Your Brand",
  companyWebsite: "https://yourbrand.com",
  contentLanguage: "English",
  industries: ["AI / Technology", "Marketing & Advertising"],
  targetAudiences: ["Entrepreneurs / Startups", "Tech-savvy users"],
  brandMission:
    "Empower creators with AI-driven content that resonates at scale.",
  brandArchetype: ["The Creator", "The Visionary"],
  brandValues: ["Innovation", "Authenticity", "Transparency"],
  brandPersonality: ["Bold", "Creative"],
  writingVoice: ["Curious storyteller", "Inspirational"],
  avoidContent: ["Fear-based language", "Hype / exaggeration"],
  emotionalToneRange: "Balanced",
  sentenceComplexity: "Medium",
  services: ["AI Content Platform", "Brand Strategy"],
  valueProposition:
    "Ship more content, faster, without losing your brand voice.",
  positioningStyle: ["Innovative", "Expert-driven"],
  problemSolved: "Brands spend 40+ hrs/week on content that doesn't perform.",
  competitors: "Jasper, Copy.ai",
  competitiveAdvantage: "Trend-aware content generation.",
  visualStyle: ["Cinematic", "Minimalist"],
  colorPalette: {
    primary: "#6c7ef8",
    secondary: "#34d399",
    background: "#111318",
    accent: "#f59e0b",
  },
  logoPlacement: "Bottom-right",
  platformPreferences: {
    instagram: "Authentic",
    facebook: "Community-focused",
    linkedIn: "Thought leadership",
    twitter: "Conversational",
    newsletter: "Storytelling",
    blog: "Thought-provoking",
  },
  campaignContext: {
    currentCampaignName: "Q1 Growth Push",
    primaryEmotionalTheme: "Growth",
    seasonalContext: "Evergreen",
  },
  brandPromise: "Every post will sound like you, never like a robot.",
  brandEnemy:
    "Generic corporate speak that performs for algorithms but means nothing to humans.",
  coreBeliefs:
    "1. Trends are signals, not scripts.\n2. Voice > volume.\n3. Content should earn attention.",
  primaryCtaPerPlatform:
    "Instagram: DM 'START'. LinkedIn: Book a demo. Newsletter: Click the case study.",
  claimsNotAllowed:
    "No guaranteed ROI claims. Never reference competitor pricing.",
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const servicesStringToArray = (val) => {
  if (Array.isArray(val)) return val;
  if (!val || typeof val !== "string") return [""];
  // New format: pipe-separated (resolveOthers now uses " | ")
  if (val.includes(" | ")) return val.split(" | ").map((s) => s.trim()).filter(Boolean);
  // Old format: comma-separated (old resolveOthers used ", ") — commas were always delimiters in old data
  if (val.includes(",")) return val.split(",").map((s) => s.trim()).filter(Boolean);
  return [val.trim()];
};

const normaliseProfile = (raw = {}) => {
  const p = { ...raw };
  // backend stores logo under "logo" key — used directly
  if (typeof p.services === "string")
    p.services = servicesStringToArray(p.services);
  if (!Array.isArray(p.services) || p.services.length === 0) p.services = [""];
  Object.keys(MULTI_FIELDS).forEach((k) => {
    if (typeof p[k] === "string")
      p[k] = p[k]
        ? p[k]
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
    if (!Array.isArray(p[k])) p[k] = [];
  });
  if (!p.colorPalette || typeof p.colorPalette !== "object")
    p.colorPalette = {
      primary: "#6c7ef8",
      secondary: "#34d399",
      background: "#111318",
      accent: "#f59e0b",
    };
  if (!p.platformPreferences || typeof p.platformPreferences !== "object")
    p.platformPreferences = {};
  if (!p.campaignContext || typeof p.campaignContext !== "object")
    p.campaignContext = {};
  if (
    !p.platformPreferencesOther ||
    typeof p.platformPreferencesOther !== "object"
  )
    p.platformPreferencesOther = {};
  return p;
};

// ─── ATOMS ───────────────────────────────────────────────────────────────────
function FieldLabel({ children, optional = false, tooltip }) {
  return (
    <div className="f-label">
      {children}
      {optional && (
        <span
          style={{
            fontSize: 9.5,
            color: D.textMuted,
            fontWeight: 500,
            background: "rgba(255,255,255,0.04)",
            padding: "1px 5px",
            borderRadius: 4,
            border: `1px solid ${D.border}`,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          optional
        </span>
      )}
      {tooltip && (
        <Tooltip title={tooltip} placement="right" arrow>
          <InfoOutlinedIcon
            sx={{
              fontSize: "13px !important",
              color: D.textMuted,
              cursor: "help",
            }}
          />
        </Tooltip>
      )}
    </div>
  );
}

function FieldValue({ value, accent }) {
  const color = accent || D.accent;
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  )
    return <div className="f-val empty">Not set</div>;
  if (Array.isArray(value))
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {value.map((v) => (
          <Chip
            key={v}
            label={v}
            size="small"
            sx={{
              background: `${color}14 !important`,
              color: `${color} !important`,
              border: `1px solid ${color}28 !important`,
            }}
          />
        ))}
      </div>
    );
  return <div className="f-val">{String(value)}</div>;
}

function MultiSelectEdit({ value = [], options, onChange }) {
  return (
    <FormControl fullWidth>
      <Select
        multiple
        value={value}
        onChange={(e) => e.target.value.length <= 5 && onChange(e.target.value)}
        renderValue={(sel) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
            {sel.map((v) => (
              <Chip
                key={v}
                label={v}
                size="small"
                sx={{
                  background: `${D.accent}14 !important`,
                  color: `${D.accent} !important`,
                  border: `1px solid ${D.accentBorder} !important`,
                }}
              />
            ))}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: "12px",
              background: "#1a1d28",
              border: `1px solid ${D.border}`,
              maxHeight: 300,
              mt: 0.5,
            },
          },
        }}
      >
        {options.map((o) => (
          <MenuItem
            key={o}
            value={o}
            disabled={value.length >= 5 && !value.includes(o)}
            sx={
              o === "Other"
                ? {
                    borderTop: `1px solid rgba(255,255,255,0.07)`,
                    mt: 0.5,
                    pt: 1,
                    fontStyle: "italic",
                    color: `${D.accent} !important`,
                  }
                : {}
            }
          >
            <Checkbox
              checked={value.includes(o)}
              size="small"
              sx={{ p: 0.5, mr: 1 }}
            />
            <ListItemText
              primary={o}
              primaryTypographyProps={{
                sx: { fontSize: 13.5, color: "#c0c7de" },
              }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function SimpleSelect({ value, options, onChange, placeholder = "Select…" }) {
  return (
    <FormControl fullWidth>
      <Select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: "12px",
              background: "#1a1d28",
              border: `1px solid ${D.border}`,
              maxHeight: 300,
              mt: 0.5,
            },
          },
        }}
      >
        <MenuItem value="">
          <Typography sx={{ color: D.textMuted, fontSize: 13 }}>
            — None —
          </Typography>
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function OptionalAccordion({ title, badge, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="optional-accordion">
      <div
        className="optional-accordion-head"
        onClick={() => setOpen((o) => !o)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <LockOpenOutlinedIcon
            sx={{ fontSize: "15px !important", color: D.accent }}
          />
          <span
            style={{
              fontFamily: D.fontBody,
              fontWeight: 600,
              fontSize: 13.5,
              color: D.textSec,
            }}
          >
            {title}
          </span>
          {badge && (
            <span
              style={{
                fontSize: 10,
                fontFamily: D.fontMono,
                fontWeight: 600,
                color: D.accent,
                background: `${D.accent}14`,
                border: `1px solid ${D.accentBorder}`,
                padding: "2px 7px",
                borderRadius: 5,
              }}
            >
              {badge}
            </span>
          )}
        </div>
        {open ? (
          <KeyboardArrowUpIcon
            sx={{ color: D.textMuted, fontSize: "18px !important" }}
          />
        ) : (
          <KeyboardArrowDownIcon
            sx={{ color: D.textMuted, fontSize: "18px !important" }}
          />
        )}
      </div>
      <Collapse in={open}>
        <div
          style={{
            padding: "14px 18px 18px",
            borderTop: `1px solid rgba(255,255,255,0.06)`,
          }}
        >
          {children}
        </div>
      </Collapse>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    active: { label: "ACTIVE", cls: "active", dot: D.success },
    trialing: { label: "TRIAL", cls: "active", dot: D.success },
    canceling: { label: "CANCELS AT PERIOD END", cls: "canceled", dot: D.warn },
    canceled: { label: "CANCELED", cls: "expired", dot: D.danger },
    past_due: { label: "PAST DUE", cls: "expired", dot: D.danger },
    unpaid: { label: "UNPAID", cls: "expired", dot: D.danger },
    none: { label: "NO SUBSCRIPTION", cls: "none", dot: D.textMuted },
  };
  const s = map[status] || map.none;
  return (
    <span className={`status-chip ${s.cls}`}>
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: s.dot,
          display: "inline-block",
          animation: s.cls === "active" ? "pulse 2.5s ease infinite" : "none",
        }}
      />
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE SECTION
// ─────────────────────────────────────────────────────────────────────────────
function ProfileSection({ section, profile, onSave, saving }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [sectionErrors, setSectionErrors] = useState({});

  const startEdit = () => {
    const snap = {};
    section.fields.forEach((f) => {
      const v = profile[f];
      if (v !== undefined)
        snap[f] = Array.isArray(v)
          ? [...v]
          : typeof v === "object" && v !== null
            ? { ...v }
            : v;
    });
    snap.platformPreferencesOther = {
      ...(profile.platformPreferencesOther || {}),
    };
    Object.values(OTHER_FIELDS).forEach((k) => {
      if (profile[k] !== undefined) snap[k] = profile[k];
    });
    setDraft(snap);
    setEditing(true);
    setOpen(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft({});
    setLogoFile(null);
    setLogoPreview(null);
    setSectionErrors({});
  };

  const handleSave = async () => {
    const errs = {};
    section.fields.forEach((f) => {
      if (!REQUIRED_KEYS.includes(f)) return;
      const v = draft[f];
      if (f === "services") {
        if (!Array.isArray(v) || !v.some((s) => String(s || "").trim()))
          errs[f] = `${FIELD_LABELS[f] || f} is required`;
      } else if (Array.isArray(v)) {
        if (v.length === 0) errs[f] = `${FIELD_LABELS[f] || f} is required`;
      } else {
        if (!String(v || "").trim()) errs[f] = `${FIELD_LABELS[f] || f} is required`;
      }
    });
    if (Object.keys(errs).length > 0) {
      setSectionErrors(errs);
      return;
    }
    setSectionErrors({});
    await onSave(draft, logoFile);
    setEditing(false);
    setDraft({});
    setLogoFile(null);
    setLogoPreview(null);
  };

  const set = (f, v) => setDraft((d) => ({ ...d, [f]: v }));
  const setN = (f, k, v) =>
    setDraft((d) => ({ ...d, [f]: { ...(d[f] || {}), [k]: v } }));

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const r = new FileReader();
      r.onloadend = () => setLogoPreview(r.result);
      r.readAsDataURL(file);
    }
  };

  // VIEW
  const viewField = (f) => {
    const label = FIELD_LABELS[f] || f;
    const isOpt = OPTIONAL_FIELDS.has(f);

    if (f === "colorPalette")
      return (
        <div key={f} style={{ marginBottom: 20 }}>
          <FieldLabel optional={isOpt}>{label}</FieldLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {COLOR_KEYS.map((k) => {
              const hex = profile.colorPalette?.[k];
              return (
                <div key={k} className="color-swatch">
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 5,
                      background: hex || "transparent",
                      border: `1px solid rgba(255,255,255,0.15)`,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: D.fontMono,
                      fontSize: 11,
                      color: D.textSec,
                    }}
                  >
                    {k.charAt(0).toUpperCase() + k.slice(1)}:{" "}
                    <span style={{ color: hex ? D.textPrimary : D.textMuted }}>
                      {hex || "—"}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
          <div className="f-label" style={{ marginTop: 14, marginBottom: 8 }}>Brand Logo</div>
          {profile.logo ? (
            <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)", border: `1px solid ${D.border}`, display: "inline-block" }}>
              <img src={profile.logo} alt="Logo" style={{ maxWidth: 110, maxHeight: 70, objectFit: "contain" }} />
            </div>
          ) : (
            <div className="f-val empty">No logo uploaded</div>
          )}
        </div>
      );

    if (f === "platformPreferences")
      return (
        <div key={f} style={{ marginBottom: 20 }}>
          <FieldLabel>{label}</FieldLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))",
              gap: 10,
            }}
          >
            {Object.entries(PLATFORM_META).map(([k, [ico, name]]) => {
              const raw = profile.platformPreferences?.[k];
              const display =
                raw === "Other"
                  ? profile.platformPreferencesOther?.[k] || "Custom tone"
                  : raw;
              return (
                <div
                  key={k}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: "rgba(255,255,255,0.025)",
                    border: `1px solid ${D.border}`,
                  }}
                >
                  <div
                    style={{
                      fontFamily: D.fontMono,
                      fontSize: 10,
                      color: D.textMuted,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      marginBottom: 5,
                    }}
                  >
                    {ico} {name}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: display ? D.textSec : D.textMuted,
                      fontStyle: display ? "normal" : "italic",
                    }}
                  >
                    {display || "Not set"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );

    if (f === "campaignContext") {
      const cc = profile.campaignContext || {};
      return (
        <div key={f} style={{ marginBottom: 20 }}>
          <FieldLabel optional>{label}</FieldLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
              gap: 10,
            }}
          >
            {[
              ["Name", cc.currentCampaignName],
              ["Theme", cc.primaryEmotionalTheme],
              ["Seasonal", cc.seasonalContext],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.025)",
                  border: `1px solid ${D.border}`,
                }}
              >
                <div
                  style={{
                    fontFamily: D.fontMono,
                    fontSize: 10,
                    color: D.textMuted,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    marginBottom: 4,
                  }}
                >
                  {k}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: v ? D.textSec : D.textMuted,
                    fontStyle: v ? "normal" : "italic",
                  }}
                >
                  {v || "Not set"}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (f === "services") {
      const list = Array.isArray(profile.services)
        ? profile.services
        : servicesStringToArray(profile.services);
      return (
        <div key={f} style={{ marginBottom: 20 }}>
          <FieldLabel>{label}</FieldLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {list.filter(Boolean).map((s, i) => (
              <Chip
                key={i}
                label={s}
                size="small"
                sx={{
                  background: `${D.success}12 !important`,
                  color: `${D.success} !important`,
                  border: `1px solid rgba(52,211,153,0.22) !important`,
                }}
              />
            ))}
            {list.filter(Boolean).length === 0 && (
              <span
                style={{
                  color: D.textMuted,
                  fontStyle: "italic",
                  fontSize: 13,
                }}
              >
                Not set
              </span>
            )}
          </div>
        </div>
      );
    }

    if (MULTI_FIELDS[f]) {
      const val = profile[f] || [];
      const otherKey = OTHER_FIELDS[f];
      const resolved =
        otherKey && val.includes("Other") && profile[otherKey]
          ? val.map((v) => (v === "Other" ? profile[otherKey] : v))
          : val;
      return (
        <div key={f} style={{ marginBottom: 20 }}>
          <FieldLabel optional={isOpt}>{label}</FieldLabel>
          <FieldValue value={resolved} />
        </div>
      );
    }

    return (
      <div key={f} style={{ marginBottom: 20 }}>
        <FieldLabel optional={isOpt}>{label}</FieldLabel>
        <FieldValue value={profile[f]} />
      </div>
    );
  };

  // EDIT
  const editField = (f) => {
    const label = FIELD_LABELS[f] || f;
    const isOpt = OPTIONAL_FIELDS.has(f);

    if (MULTI_FIELDS[f]) {
      const otherKey = OTHER_FIELDS[f];
      const showOther = otherKey && (draft[f] || []).includes("Other");
      return (
        <div key={f} style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <FieldLabel optional={isOpt}>
              {label}{" "}
              <span
                style={{
                  color: D.textMuted,
                  fontSize: 10,
                  fontFamily: D.fontBody,
                  textTransform: "none",
                  letterSpacing: 0,
                }}
              >
                up to 5
              </span>
            </FieldLabel>
            {(draft[f] || []).length > 0 && (
              <span
                onClick={() => { set(f, []); if (OTHER_FIELDS[f]) set(OTHER_FIELDS[f], ""); }}
                style={{ fontSize: 12, color: D.accent, cursor: "pointer", userSelect: "none" }}
              >
                Clear
              </span>
            )}
          </div>
          {sectionErrors[f] && (
            <div style={{ color: "#f87171", fontSize: 12, marginBottom: 6 }}>{sectionErrors[f]}</div>
          )}
          <MultiSelectEdit
            value={draft[f] || []}
            options={MULTI_FIELDS[f]}
            onChange={(v) => {
              set(f, v);
              if (sectionErrors[f]) setSectionErrors((e) => ({ ...e, [f]: undefined }));
              const ok = OTHER_FIELDS[f];
              if (
                ok &&
                (draft[f] || []).includes("Other") &&
                !v.includes("Other")
              )
                set(ok, "");
            }}
          />
          {showOther && (
            <div style={{ marginTop: 10 }}>
              <FieldLabel optional>
                Describe your custom {label.toLowerCase()}
              </FieldLabel>
              <TextField
                fullWidth
                size="small"
                placeholder={`e.g. your custom ${label.toLowerCase()}`}
                value={draft[otherKey] || ""}
                inputProps={{ maxLength: 80 }}
                onChange={(e) => set(otherKey, e.target.value)}
                helperText={`${(draft[otherKey] || "").length} / 80`}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    background: `${D.accent}0a !important`,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: `${D.accentBorder} !important`,
                  },
                }}
              />
            </div>
          )}
        </div>
      );
    }

    if (f === "emotionalToneRange")
      return (
        <div key={f} style={{ marginBottom: 22 }}>
          <FieldLabel>{label}</FieldLabel>
          <RadioGroup
            row
            value={draft.emotionalToneRange || "Balanced"}
            onChange={(e) => set("emotionalToneRange", e.target.value)}
          >
            {["Calm", "Balanced", "Energetic"].map((v) => (
              <FormControlLabel
                key={v}
                value={v}
                control={<Radio size="small" />}
                label={
                  v === "Calm"
                    ? "Calm & Subtle"
                    : v === "Balanced"
                      ? "Balanced & Neutral"
                      : "Energetic & Bold"
                }
              />
            ))}
          </RadioGroup>
        </div>
      );

    if (f === "sentenceComplexity")
      return (
        <div key={f} style={{ marginBottom: 22 }}>
          <FieldLabel>{label}</FieldLabel>
          <RadioGroup
            row
            value={draft.sentenceComplexity || "Medium"}
            onChange={(e) => set("sentenceComplexity", e.target.value)}
          >
            {["Simple", "Medium", "Advanced"].map((v) => (
              <FormControlLabel
                key={v}
                value={v}
                control={<Radio size="small" />}
                label={v}
              />
            ))}
          </RadioGroup>
        </div>
      );

    if (f === "logoPlacement")
      return (
        <div key={f} style={{ marginBottom: 22 }}>
          <FieldLabel optional>{label}</FieldLabel>
          <RadioGroup
            row
            value={draft.logoPlacement || "Bottom-right"}
            onChange={(e) => set("logoPlacement", e.target.value)}
          >
            {["Bottom-left", "Bottom-right"].map(
              (v) => (
                <FormControlLabel
                  key={v}
                  value={v}
                  control={<Radio size="small" />}
                  label={v.replace("-", " ")}
                />
              ),
            )}
          </RadioGroup>
        </div>
      );

    if (f === "colorPalette")
      return (
        <div key={f} style={{ marginBottom: 22 }}>
          <FieldLabel>{label}</FieldLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))",
              gap: 20,
              marginBottom: 20,
            }}
          >
            {COLOR_KEYS.map((k) => (
              <div key={k}>
                <div className="f-label" style={{ marginBottom: 8 }}>
                  {k.charAt(0).toUpperCase() + k.slice(1)}
                </div>
                <SketchPicker
                  color={draft.colorPalette?.[k] || "#ffffff"}
                  onChange={(c) => setN("colorPalette", k, c.hex)}
                  styles={{
                    default: {
                      picker: {
                        width: "100%",
                        boxShadow: "none",
                        borderRadius: 10,
                        background: "#1a1d28",
                        border: `1px solid ${D.border}`,
                      },
                    },
                  }}
                />
                <div className="color-swatch" style={{ marginTop: 8 }}>
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      background: draft.colorPalette?.[k] || "transparent",
                      border: `1px solid rgba(255,255,255,0.12)`,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: D.fontMono,
                      fontSize: 10.5,
                      color: D.textSec,
                    }}
                  >
                    {draft.colorPalette?.[k] || "—"}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="f-label" style={{ marginTop: 4 }}>
            Brand Logo
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            {(logoPreview || profile.logo) && (
              <div
                style={{
                  padding: 10,
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.03)",
                  border: `1px solid ${D.border}`,
                }}
              >
                <img
                  src={logoPreview || profile.logo}
                  alt="Logo"
                  style={{ maxWidth: 110, maxHeight: 70, objectFit: "contain" }}
                />
              </div>
            )}
            <Button
              variant="outlined"
              component="label"
              sx={{
                borderColor: D.border,
                color: D.textSec,
                fontSize: 13,
                "&:hover": {
                  borderColor: D.accent,
                  color: D.accent,
                  background: D.accentSoft,
                },
              }}
            >
              {logoPreview ? "Replace Logo" : "Upload Logo"}
              <input
                type="file"
                hidden
                accept=".png,.jpg,.jpeg"
                onChange={handleLogoUpload}
              />
            </Button>
          </div>
        </div>
      );

    if (f === "platformPreferences") {
      const otherPlaceholders = {
        instagram: "e.g. Raw & unfiltered, Behind-the-scenes casual",
        facebook: "e.g. Nostalgic & warm, Neighbourhood-friendly",
        linkedIn: "e.g. Contrarian thought leader, Dry wit professional",
        newsletter: "e.g. Long-form philosophical, Weekly personal journal",
        twitter: "e.g. Hot takes & quick wit, Thread-based storytelling",
        blog: "e.g. Deep-dive investigative, Opinionated essayist",
      };
      return (
        <div key={f} style={{ marginBottom: 22 }}>
          <FieldLabel>{label}</FieldLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))",
              gap: 14,
            }}
          >
            {Object.entries(PLATFORM_META).map(([k, [ico, name]]) => {
              const isOtherSelected =
                draft.platformPreferences?.[k] === "Other";
              return (
                <div key={k}>
                  <div className="f-label" style={{ marginBottom: 6 }}>
                    {ico} {name}
                  </div>
                  <SimpleSelect
                    value={draft.platformPreferences?.[k] || ""}
                    options={platformToneOptions[k] || []}
                    onChange={(v) => {
                      setN("platformPreferences", k, v);
                      if (v !== "Other")
                        setDraft((d) => ({
                          ...d,
                          platformPreferencesOther: {
                            ...(d.platformPreferencesOther || {}),
                            [k]: "",
                          },
                        }));
                    }}
                    placeholder="Select tone…"
                  />
                  {isOtherSelected && (
                    <div style={{ marginTop: 8 }}>
                      <TextField
                        fullWidth
                        size="small"
                        autoFocus
                        placeholder={otherPlaceholders[k]}
                        value={draft.platformPreferencesOther?.[k] || ""}
                        inputProps={{ maxLength: 80 }}
                        onChange={(e) =>
                          setN("platformPreferencesOther", k, e.target.value)
                        }
                        helperText={`${(draft.platformPreferencesOther?.[k] || "").length} / 80`}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            background: `${D.accent}0a !important`,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: `${D.accentBorder} !important`,
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (f === "campaignContext")
      return (
        <div key={f} style={{ marginBottom: 22 }}>
          <FieldLabel optional>{label}</FieldLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
              gap: 14,
            }}
          >
            <div>
              <div className="f-label" style={{ marginBottom: 6 }}>
                Campaign Name
              </div>
              <TextField
                fullWidth
                size="small"
                value={draft.campaignContext?.currentCampaignName || ""}
                onChange={(e) =>
                  setN("campaignContext", "currentCampaignName", e.target.value)
                }
                inputProps={{ maxLength: 60 }}
              />
            </div>
            <div>
              <div className="f-label" style={{ marginBottom: 6 }}>
                Emotional Theme
              </div>
              <SimpleSelect
                value={draft.campaignContext?.primaryEmotionalTheme || ""}
                options={emotionalThemeOptions}
                onChange={(v) =>
                  setN("campaignContext", "primaryEmotionalTheme", v)
                }
              />
            </div>
            <div>
              <div className="f-label" style={{ marginBottom: 6 }}>
                Seasonal Context
              </div>
              <SimpleSelect
                value={draft.campaignContext?.seasonalContext || ""}
                options={seasonalContextOptions}
                onChange={(v) => setN("campaignContext", "seasonalContext", v)}
              />
            </div>
          </div>
        </div>
      );

    if (f === "services") {
      const svcs = draft.services || [""];
      return (
        <div key={f} style={{ marginBottom: 22 }}>
          <FieldLabel>{label}</FieldLabel>
          {svcs.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <TextField
                fullWidth
                size="small"
                value={s}
                inputProps={{ maxLength: 150 }}
                onChange={(e) => {
                  const u = [...svcs];
                  u[i] = e.target.value;
                  set("services", u);
                }}
                helperText={`${s.length}/150`}
              />
              {svcs.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const u = [...svcs];
                    u.splice(i, 1);
                    set("services", u);
                  }}
                  sx={{ color: D.danger, mt: 0.25 }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => set("services", [...svcs, ""])}
            sx={{
              color: D.success,
              fontSize: 12.5,
              "&:hover": { background: D.successSoft },
            }}
          >
            Add Another
          </Button>
        </div>
      );
    }

    if (f === "funnelStageFocus")
      return (
        <div key={f} style={{ marginBottom: 22 }}>
          <FieldLabel optional>{label}</FieldLabel>
          <SimpleSelect
            value={draft.funnelStageFocus || ""}
            options={funnelStageOptions}
            onChange={(v) => set("funnelStageFocus", v)}
            placeholder="Select funnel stage…"
          />
        </div>
      );

    const isTextarea = TEXTAREA_FIELDS.has(f);
    const maxLen = isTextarea ? 800 : 100;
    const val = typeof draft[f] === "string" ? draft[f] : draft[f] || "";
    return (
      <div key={f} style={{ marginBottom: 22 }}>
        <FieldLabel optional={isOpt}>{label}</FieldLabel>
        {sectionErrors[f] && (
          <div style={{ color: "#f87171", fontSize: 12, marginBottom: 6 }}>{sectionErrors[f]}</div>
        )}
        <TextField
          fullWidth
          multiline={isTextarea}
          rows={isTextarea ? 4 : 1}
          value={val}
          onChange={(e) => {
            set(f, e.target.value);
            if (sectionErrors[f]) setSectionErrors((e2) => ({ ...e2, [f]: undefined }));
          }}
          inputProps={{ maxLength: maxLen }}
          helperText={`${val.length} / ${maxLen}`}
          placeholder={
            isOpt
              ? "Skip if you're just starting out — you can add this later."
              : undefined
          }
        />
      </div>
    );
  };

  const REQUIRED_Q = ["brandPromise", "brandEnemy", "coreBeliefs"];
  const OPTIONAL_Q = [
    "voiceReferenceExamples",
    "exactAudienceLanguage",
    "signaturePhrases",
    "audienceFears",
    "customerTransformationStory",
    "socialProofData",
    "funnelStageFocus",
    "funnelStageBreakdown",
    "primaryCtaPerPlatform",
    "claimsNotAllowed",
  ];
  const OPTIONAL_Q_META = {
    voiceReferenceExamples: {
      title: "Q04 — Voice References",
      badge: "voice calibration",
      hint: "Paste 2–3 sentences from past content that felt exactly right.",
    },
    exactAudienceLanguage: {
      title: "Q05 — Exact Audience Language",
      badge: "hook generation",
      hint: "10–15 real phrases your ideal customer actually says.",
    },
    signaturePhrases: {
      title: "Q06 — Signature Phrases",
      badge: "voice calibration",
      hint: "10–15 expressions or vocabulary words this brand uses repeatedly.",
    },
    audienceFears: {
      title: "Q07 — Audience's Top 5 Fears",
      badge: "emotional hooks",
      hint: "The deeper fears your ideal customer might not admit.",
    },
    customerTransformationStory: {
      title: "Q08 — The Transformation Story",
      badge: "storytelling",
      hint: "Before state → after state, one real customer.",
    },
    socialProofData: {
      title: "Q09 — Social Proof Numbers",
      badge: "authority content",
      hint: "Your best credibility numbers. Skip if pre-launch.",
    },
    funnelStageFocus: {
      title: "Q11 — Funnel Stage Focus",
      badge: "CTA intensity",
      hint: "Where is most of your audience in their buying journey?",
    },
    funnelStageBreakdown: {
      title: "Q11b — Funnel % Breakdown",
      badge: "CTA intensity",
      hint: "Rough % split e.g. '60% cold, 30% warm, 10% hot'",
    },
    primaryCtaPerPlatform: {
      title: "Q10 — Primary CTA Per Platform",
      badge: "conversion",
      hint: "The single action you want users to take on each platform. e.g. 'Instagram: DM START · LinkedIn: Book a demo'",
    },
    claimsNotAllowed: {
      title: "Q12 — Hard Guardrails",
      badge: "compliance",
      hint: "Claims or statements your brand must never make. e.g. 'No guaranteed ROI claims. Never reference competitor pricing.'",
    },
  };

  const isStrategy = section.id === "strategy";

  return (
    <div className={`prof-section${editing ? " editing" : ""}`}>
      <div
        className="prof-section-head"
        onClick={() => !editing && setOpen((o) => !o)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              background: D.accentSoft,
              border: `1px solid ${D.accentBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 17,
              flexShrink: 0,
            }}
          >
            {section.icon}
          </div>
          <div>
            <div
              style={{
                fontFamily: D.fontDisplay,
                fontWeight: 700,
                fontSize: 14.5,
                color: D.textPrimary,
                lineHeight: 1.2,
              }}
            >
              {section.title}
            </div>
            <div style={{ fontSize: 12.5, color: D.textSec, marginTop: 2 }}>
              {section.subtitle}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!editing && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                startEdit();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "6px 13px",
                borderRadius: 8,
                border: `1px solid ${D.border}`,
                background: "rgba(255,255,255,0.04)",
                color: D.textSec,
                fontSize: 12.5,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.15s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = D.accentBorder;
                e.currentTarget.style.color = D.accent;
                e.currentTarget.style.background = D.accentSoft;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = D.border;
                e.currentTarget.style.color = D.textSec;
                e.currentTarget.style.background = "rgba(255,255,255,0.04)";
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: "13px !important" }} /> Edit
            </button>
          )}
          {!editing &&
            (open ? (
              <KeyboardArrowUpIcon
                sx={{ color: D.textMuted, fontSize: "20px !important" }}
              />
            ) : (
              <KeyboardArrowDownIcon
                sx={{ color: D.textMuted, fontSize: "20px !important" }}
              />
            ))}
        </div>
      </div>

      {editing && (
        <div className="edit-bar">
          <Typography
            sx={{
              fontSize: 12.5,
              color: D.accent,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: "13px !important" }} /> Unsaved
            changes
          </Typography>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              size="small"
              onClick={cancelEdit}
              sx={{
                color: D.textSec,
                fontSize: 12,
                "&:hover": {
                  color: D.textPrimary,
                  background: "rgba(255,255,255,0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={handleSave}
              disabled={saving}
              startIcon={
                saving ? (
                  <span className="spin" />
                ) : (
                  <SaveIcon sx={{ fontSize: "13px !important" }} />
                )
              }
              sx={{
                background: `${D.accent} !important`,
                color: "white",
                px: 2,
                fontSize: 12.5,
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(108,126,248,0.3) !important",
                },
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </div>
      )}

      <Collapse in={open || editing}>
        <div
          className="prof-section-body"
          style={{ paddingTop: editing ? 20 : 16 }}
        >
          {isStrategy ? (
            editing ? (
              <div>
                <div
                  style={{
                    fontFamily: D.fontMono,
                    fontSize: 10,
                    color: D.accent,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  🔒 Required Fields
                </div>
                {REQUIRED_Q.map((f) => editField(f))}
                <Divider sx={{ my: 2.5 }} />
                <div
                  style={{
                    fontFamily: D.fontMono,
                    fontSize: 10,
                    color: D.textMuted,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  🔓 Optional Fields — add when you have traction
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {OPTIONAL_Q.map((f) => {
                    const meta = OPTIONAL_Q_META[f];
                    return (
                      <OptionalAccordion
                        key={f}
                        title={meta.title}
                        badge={meta.badge}
                      >
                        <p
                          style={{
                            fontSize: 12.5,
                            color: D.textMuted,
                            lineHeight: 1.6,
                            margin: "0 0 12px",
                          }}
                        >
                          {meta.hint}
                        </p>
                        {editField(f)}
                      </OptionalAccordion>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div>
                <div
                  style={{
                    fontFamily: D.fontMono,
                    fontSize: 10,
                    color: D.accent,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: 14,
                  }}
                >
                  🔒 Required
                </div>
                {REQUIRED_Q.map((f) => viewField(f))}
                <Divider sx={{ my: 2 }} />
                <div
                  style={{
                    fontFamily: D.fontMono,
                    fontSize: 10,
                    color: D.textMuted,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: 14,
                  }}
                >
                  🔓 Optional
                </div>
                {OPTIONAL_Q.map((f) => viewField(f))}
              </div>
            )
          ) : editing ? (
            section.fields.map((f) => editField(f))
          ) : (
            section.fields.map((f) => viewField(f))
          )}
        </div>
      </Collapse>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// USAGE TAB — receives data as props, no internal fetching
// ─────────────────────────────────────────────────────────────────────────────
function UsageTab({ usage, loading, userFlags = {} }) {
  const { hasEbook = false, hasEmailSequences = false, hasVideo = false } = userFlags;
  const metrics = [
    {
      label: "Trend Searches",
      rem: "remaining_trend_searches",
      tot: "total_trend_searches",
      addon: "trend_searches",
      icon: "🔍",
      color: D.accent,
    },
    {
      label: "Content Generations",
      rem: "remaining_content_generations",
      tot: "total_content_generations",
      addon: "content_generations",
      icon: "⚡",
      color: D.success,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))",
          gap: 16,
          marginBottom: 28,
        }}
      >
        {metrics.map((m) => {
          const rem = usage[m.rem] ?? 0;
          const tot = usage[m.tot] ?? 0;
          const pct = tot > 0 ? Math.round((rem / tot) * 100) : 0;
          const addOnCredits = usage?.add_ons?.[m.addon] || 0;
          return (
            <div key={m.label} className="metric-card">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 16,
                }}
              >
                <div>
                  <div
                    style={{
                      fontFamily: D.fontMono,
                      fontSize: 10.5,
                      color: D.textMuted,
                      letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      marginBottom: 8,
                    }}
                  >
                    {m.label}
                  </div>
                  {loading ? (
                    <div className="sk" style={{ width: 80, height: 32 }} />
                  ) : (
                    <div
                      style={{
                        fontFamily: D.fontDisplay,
                        fontWeight: 800,
                        fontSize: 36,
                        color: D.textPrimary,
                        lineHeight: 1,
                      }}
                    >
                      {rem.toLocaleString()}
                      <span
                        style={{
                          fontFamily: D.fontBody,
                          fontSize: 13,
                          color: D.textSec,
                          fontWeight: 400,
                          marginLeft: 6,
                        }}
                      >
                        / {tot.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 11,
                    background: `${m.color}14`,
                    border: `1px solid ${m.color}28`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                  }}
                >
                  {m.icon}
                </div>
              </div>
              <LinearProgress
                variant="determinate"
                value={loading ? 0 : pct}
                sx={{
                  mb: 0.75,
                  "& .MuiLinearProgress-bar": { background: m.color },
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span
                  style={{
                    fontFamily: D.fontMono,
                    fontSize: 10.5,
                    color: D.textSec,
                  }}
                >
                  Remaining this period
                </span>
                <span
                  style={{
                    fontFamily: D.fontMono,
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: m.color,
                  }}
                >
                  {loading ? "—" : pct + "%"}
                </span>
              </div>
              {addOnCredits > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "6px 10px",
                    borderRadius: 7,
                    background: `${D.success}0d`,
                    border: `1px solid rgba(52,211,153,0.2)`,
                  }}
                >
                  <span
                    style={{
                      fontFamily: D.fontMono,
                      fontSize: 10.5,
                      color: D.success,
                      fontWeight: 600,
                    }}
                  >
                    + {addOnCredits} add-on credit
                    {addOnCredits !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Credit balances for credit-based features ── */}
      {(hasEbook || hasEmailSequences || hasVideo) && (
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontFamily: D.fontMono,
              fontSize: 10.5,
              color: D.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 12,
              fontWeight: 600,
            }}
          >
            Add-on Credit Balances
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
              gap: 12,
            }}
          >
            {[
              { key: "ebook_credits", label: "eBook Credits", icon: "📖", color: "#8b5cf6", enabled: hasEbook },
              { key: "email_sequence_credits", label: "Email Sequence Credits", icon: "✉️", color: "#f59e0b", enabled: hasEmailSequences },
              { key: "video_credits", label: "Video Credits", icon: "🎬", color: "#ec4899", enabled: hasVideo },
            ]
              .filter((c) => c.enabled)
              .map((c) => (
                <div
                  key={c.key}
                  style={{
                    padding: "16px 18px",
                    borderRadius: 12,
                    background: D.surface,
                    border: `1px solid ${D.border}`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        background: `${c.color}15`,
                        border: `1px solid ${c.color}28`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 16,
                      }}
                    >
                      {c.icon}
                    </div>
                    <div
                      style={{
                        fontFamily: D.fontMono,
                        fontSize: 10,
                        color: D.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        fontWeight: 600,
                      }}
                    >
                      {c.label}
                    </div>
                  </div>
                  <div
                    style={{
                      fontFamily: D.fontDisplay,
                      fontWeight: 800,
                      fontSize: 32,
                      color: c.color,
                      lineHeight: 1,
                    }}
                  >
                    {loading ? "—" : usage[c.key]}
                  </div>
                  <div style={{ fontSize: 11, color: D.textMuted, marginTop: 4 }}>
                    credits remaining
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {usage?.period_end && (
        <div
          style={{
            fontFamily: D.fontMono,
            fontSize: 11.5,
            color: D.textMuted,
            marginBottom: 20,
          }}
        >
          Period resets:{" "}
          {new Date(usage.period_end).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </div>
      )}

      {usage?.addon_history?.length > 0 && (
        <div
          style={{
            background: D.surface,
            border: `1px solid ${D.border}`,
            borderRadius: 14,
            padding: "20px 24px",
          }}
        >
          <div
            style={{
              fontFamily: D.fontMono,
              fontSize: 10.5,
              color: D.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 14,
              fontWeight: 600,
            }}
          >
            Add-on Purchase History
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {usage.addon_history.map((h, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  borderRadius: 9,
                  background: "rgba(255,255,255,0.025)",
                  border: `1px solid ${D.border}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 16 }}>
                    {h.type === "trend_search_topup" ? "🔍"
                      : h.type === "ebook_topup" ? "📖"
                      : h.type === "email_sequence_topup" ? "✉️"
                      : h.type === "video_topup" ? "🎬"
                      : "⚡"}
                  </span>
                  <div>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: D.textPrimary,
                      }}
                    >
                      {h.quantity}x{" "}
                      {h.type === "trend_search_topup" ? "Trend Searches"
                        : h.type === "ebook_topup" ? "eBook Credits"
                        : h.type === "email_sequence_topup" ? "Email Sequence Credits"
                        : h.type === "video_topup" ? "Video Credits"
                        : "Content Generations"}
                    </div>
                    <div
                      style={{
                        fontFamily: D.fontMono,
                        fontSize: 10.5,
                        color: D.textMuted,
                      }}
                    >
                      {new Date(h.credited_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                </div>
                <span
                  style={{
                    fontFamily: D.fontMono,
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: D.success,
                    background: `${D.success}0d`,
                    border: `1px solid rgba(52,211,153,0.2)`,
                    padding: "3px 9px",
                    borderRadius: 6,
                  }}
                >
                  credited
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUBSCRIPTION TAB — self-contained, fetches own data
// ─────────────────────────────────────────────────────────────────────────────
function SubscriptionTab({ email }) {
  const [sub, setSub] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [resubLoading, setResubLoading] = useState(false);

  const loadSub = async () => {
    setSubLoading(true);
    setSubError("");
    try {
      const res = await api.get("/auth/subscription");
      setSub(res.data);
    } catch {
      setSubError("Could not load subscription. Try refreshing.");
    } finally {
      setSubLoading(false);
    }
  };

  // ✅ FIX: stable dep — only run once when email is available
  useEffect(() => {
    if (email) loadSub();
  }, [email]); // eslint-disable-line

  const handleCancel = async () => {
    if (
      !window.confirm(
        "Cancel your subscription? You'll keep access until the end of your billing period.",
      )
    )
      return;
    setCancelLoading(true);
    try {
      const res = await api.post("/auth/cancel-subscription");
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || `HTTP ${res.status}`);
      }
      setCancelSuccess(true);
      // ✅ FIX: update local state immediately, don't wait for webhook
      setSub((prev) =>
        prev
          ? { ...prev, status: "canceling", cancel_at_period_end: true }
          : prev,
      );
    } catch (e) {
      alert("Cancellation failed: " + e.message);
    } finally {
      setCancelLoading(false);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/auth/billing-portal");
      window.location.href = res.data.url;
    } catch (e) {
      alert("Could not open billing portal: " + e.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleResub = async () => {
    setResubLoading(true);
    try {
      const res = await api.post("/auth/create-subscription-checkout");
      window.location.href = res.data.url;
    } catch (e) {
      alert("Could not start resubscription: " + e.message);
    } finally {
      setResubLoading(false);
    }
  };

  const subStatus = !sub
    ? "none"
    : sub.status === "active" && sub.cancel_at_period_end
      ? "canceling"
      : sub.status || "none";
  const isCanceled = subStatus === "canceled" || subStatus === "expired";

  if (subLoading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 48,
        }}
      >
        <span
          className="spin"
          style={{ width: 22, height: 22, borderWidth: 2.5 }}
        />
      </div>
    );

  if (subError)
    return (
      <div
        style={{
          padding: 20,
          borderRadius: 12,
          background: D.dangerSoft,
          border: `1px solid rgba(248,113,113,0.25)`,
        }}
      >
        <span style={{ color: D.danger }}>{subError}</span>
        <button
          onClick={loadSub}
          style={{
            marginLeft: 14,
            padding: "6px 14px",
            borderRadius: 8,
            border: `1px solid ${D.border}`,
            background: "transparent",
            color: D.textSec,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Retry
        </button>
      </div>
    );

  return (
    <div
      style={{
        background: D.surface,
        border: `1px solid ${D.border}`,
        borderRadius: 14,
        padding: "28px 28px 24px",
      }}
    >
      <div
        style={{
          fontFamily: D.fontDisplay,
          fontWeight: 700,
          fontSize: 16,
          color: D.textPrimary,
          marginBottom: 22,
        }}
      >
        Subscription Details
      </div>

      {cancelSuccess && (
        <div
          style={{
            marginBottom: 20,
            padding: "14px 18px",
            borderRadius: 12,
            background: D.warnSoft,
            border: `1px solid rgba(245,158,11,0.3)`,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              color: D.warn,
              fontSize: 14,
              marginBottom: 4,
            }}
          >
            Cancellation confirmed
          </div>
          <div style={{ fontSize: 13, color: D.textSec }}>
            You'll keep access until the end of your current billing period.
          </div>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <div className="f-label">Status</div>
          <StatusChip status={subStatus} />
        </div>
        {/* Plan display hidden — backend "plan" field isn't reliably populated yet
        <div>
          <div className="f-label">Plan</div>
          <div style={{ fontSize: 14, color: D.textPrimary, fontWeight: 600 }}>
            Standard
          </div>
        </div>
        */}
        {sub?.current_period_end && (
          <div>
            <div className="f-label">
              {subStatus === "canceling" ? "Access Until" : "Next Renewal"}
            </div>
            <div
              style={{ fontSize: 14, color: D.textPrimary, fontWeight: 600 }}
            >
              {new Date(sub.current_period_end).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
          </div>
        )}
        {sub?.created_at && (
          <div>
            <div className="f-label">Member Since</div>
            <div
              style={{ fontSize: 14, color: D.textPrimary, fontWeight: 600 }}
            >
              {new Date(sub.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
          </div>
        )}
      </div>

      {subStatus === "canceling" && (
        <div
          style={{
            marginBottom: 20,
            padding: "12px 16px",
            borderRadius: 10,
            background: D.warnSoft,
            border: `1px solid rgba(245,158,11,0.25)`,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: D.warn,
              fontSize: 13,
              marginBottom: 3,
            }}
          >
            Scheduled for cancellation
          </div>
          <div style={{ fontSize: 12.5, color: D.textSec }}>
            Ends{" "}
            {sub?.current_period_end
              ? new Date(sub.current_period_end).toLocaleDateString()
              : "at period end"}
            . Full access until then.
          </div>
        </div>
      )}

      {!sub?.subscription_id || subStatus === "none" ? (
        <button
          onClick={handleResub}
          disabled={resubLoading}
          style={{
            padding: "11px 24px",
            borderRadius: 10,
            border: "none",
            background: `linear-gradient(135deg,${D.accent},${D.purple})`,
            color: "white",
            fontFamily: D.fontBody,
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            opacity: resubLoading ? 0.5 : 1,
          }}
        >
          {resubLoading ? "Loading…" : "Subscribe Now"}
        </button>
      ) : (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={handlePortal}
            disabled={portalLoading}
            style={{
              padding: "10px 22px",
              borderRadius: 10,
              border: `1px solid ${D.border}`,
              background: "transparent",
              color: D.textSec,
              fontFamily: D.fontBody,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              opacity: portalLoading ? 0.5 : 1,
            }}
          >
            {portalLoading ? "Loading…" : "Manage Payment Method"}
          </button>
          {isCanceled ? (
            <button
              onClick={handleResub}
              disabled={resubLoading}
              style={{
                padding: "11px 24px",
                borderRadius: 10,
                border: "none",
                background: `linear-gradient(135deg,${D.accent},${D.purple})`,
                color: "white",
                fontFamily: D.fontBody,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                opacity: resubLoading ? 0.5 : 1,
              }}
            >
              {resubLoading ? "Loading…" : "Resubscribe"}
            </button>
          ) : subStatus !== "canceling" ? (
            <button
              onClick={handleCancel}
              disabled={cancelLoading}
              style={{
                padding: "10px 22px",
                borderRadius: 10,
                border: `1px solid rgba(248,113,113,0.3)`,
                background: "rgba(248,113,113,0.07)",
                color: D.danger,
                fontFamily: D.fontBody,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                opacity: cancelLoading ? 0.5 : 1,
              }}
            >
              {cancelLoading ? "Processing…" : "Cancel Subscription"}
            </button>
          ) : (
            <span
              style={{
                fontFamily: D.fontMono,
                fontSize: 12.5,
                color: D.textMuted,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              ✓ Cancellation scheduled
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TOP-UPS TAB — self-contained
// ─────────────────────────────────────────────────────────────────────────────
function TopupsTab({ email, userFlags = {} }) {
  const { hasEbook = false, hasEmailSequences = false, hasVideo = false } = userFlags;
  const navigate = useNavigate();
  const [topupType, setTopupType] = useState("trend_search_topup");
  const [topupQty, setTopupQty] = useState(5);
  const [topupLoading, setTopupLoading] = useState(false);

  const handleTopup = async () => {
    if (!email) {
      alert("No email found. Please log in again.");
      navigate("/login");
      return;
    }
    if (topupQty < 1 || topupQty > 100) {
      alert("Quantity must be between 1 and 100");
      return;
    }
    setTopupLoading(true);
    try {
      // ✅ FIX: correct API path, correct body shape
      const res = await api.post("/auth/create-topup-session", {
        type: topupType,
        quantity: topupQty,
      });
      window.location.href = res.data.url;
    } catch (e) {
      alert("Top-up failed: " + e.message);
    } finally {
      setTopupLoading(false);
    }
  };

  const types = [
    {
      id: "trend_search_topup",
      icon: "🔍",
      label: "Trend Searches",
      sub: "Find trending topics",
      color: D.accent,
    },
    {
      id: "content_generation_topup",
      icon: "🚀",
      label: "Content Generations",
      sub: "Generate full content packages",
      color: D.success,
    },
    ...(hasEbook ? [{
      id: "ebook_topup",
      icon: "📖",
      label: "eBook Credits",
      sub: "Generate full eBooks",
      color: "#8b5cf6",
    }] : []),
    ...(hasEmailSequences ? [{
      id: "email_sequence_topup",
      icon: "✉️",
      label: "Email Sequence Credits",
      sub: "Generate 7-email sequences",
      color: "#f59e0b",
    }] : []),
    ...(hasVideo ? [{
      id: "video_topup",
      icon: "🎬",
      label: "Video Credits",
      sub: "Generate video scripts & assets",
      color: "#ec4899",
    }] : []),
  ];

  return (
    <div
      style={{
        background: D.surface,
        border: `1px solid ${D.border}`,
        borderRadius: 14,
        padding: "28px 28px 24px",
      }}
    >
      <div
        style={{
          fontFamily: D.fontDisplay,
          fontWeight: 700,
          fontSize: 16,
          color: D.textPrimary,
          marginBottom: 6,
        }}
      >
        Purchase Top-ups
      </div>
      <div style={{ fontSize: 13.5, color: D.textSec, marginBottom: 24 }}>
        Buy extra credits that never expire. They stack on top of your monthly
        allocation.
      </div>

      <div
        style={{
          fontFamily: D.fontMono,
          fontSize: 10.5,
          color: D.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          marginBottom: 12,
          fontWeight: 600,
        }}
      >
        Select type
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
          gap: 12,
          marginBottom: 24,
        }}
      >
        {types.map((t) => (
          <div
            key={t.id}
            className={`topup-card${topupType === t.id ? " selected" : ""}`}
            style={{ borderColor: topupType === t.id ? t.color : undefined }}
            onClick={() => setTopupType(t.id)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: `${t.color}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                {t.icon}
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: D.textPrimary,
                  }}
                >
                  {t.label}
                </div>
                <div style={{ fontSize: 12, color: D.textMuted }}>{t.sub}</div>
              </div>
              {topupType === t.id && (
                <div
                  style={{
                    marginLeft: "auto",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: t.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    color: "white",
                    flexShrink: 0,
                  }}
                >
                  ✓
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          fontFamily: D.fontMono,
          fontSize: 10.5,
          color: D.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          marginBottom: 12,
          fontWeight: 600,
        }}
      >
        Quantity
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        {[1, 3, 5, 10, 20].map((q) => (
          <div
            key={q}
            onClick={() => setTopupQty(q)}
            style={{
              width: 52,
              height: 42,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border:
                topupQty === q
                  ? `1.5px solid ${D.accent}`
                  : `1px solid ${D.border}`,
              background:
                topupQty === q ? D.accentSoft : "rgba(255,255,255,0.03)",
              color: topupQty === q ? D.accent : D.textSec,
              fontFamily: D.fontMono,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {q}
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: D.textMuted }}>or custom:</span>
          <input
            type="number"
            min={1}
            max={100}
            value={topupQty}
            onChange={(e) =>
              setTopupQty(
                Math.max(1, Math.min(100, parseInt(e.target.value) || 1)),
              )
            }
            style={{
              width: 72,
              padding: "8px 10px",
              borderRadius: 9,
              border: `1px solid ${D.border}`,
              background: "rgba(255,255,255,0.04)",
              color: D.textPrimary,
              fontFamily: D.fontMono,
              fontSize: 13,
              outline: "none",
            }}
          />
        </div>
      </div>

      <div
        style={{
          marginBottom: 20,
          padding: "14px 18px",
          borderRadius: 12,
          background: D.accentSoft,
          border: `1px solid ${D.accentBorder}`,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 13.5, color: D.textPrimary }}>
          {topupQty}x{" "}
          {topupType === "trend_search_topup" ? "Trend Search"
            : topupType === "ebook_topup" ? "eBook"
            : topupType === "email_sequence_topup" ? "Email Sequence"
            : topupType === "video_topup" ? "Video"
            : "Content Generation"}{" "}
          credit{topupQty !== 1 ? "s" : ""}
        </div>
        <div style={{ fontSize: 12, color: D.textSec, marginTop: 4 }}>
          Credits never expire and stack on top of your monthly allocation.
          One-time payment, no recurring charges.
        </div>
      </div>

      <button
        onClick={handleTopup}
        disabled={topupLoading || !email}
        style={{
          padding: "11px 24px",
          borderRadius: 10,
          border: "none",
          background: `linear-gradient(135deg,${D.accent},${D.purple})`,
          color: "white",
          fontFamily: D.fontBody,
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
          opacity: topupLoading || !email ? 0.5 : 1,
        }}
      >
        {topupLoading
          ? "Redirecting to checkout…"
          : `Buy ${topupQty} Credit${topupQty > 1 ? "s" : ""} →`}
      </button>
    </div>
  );
}

// Add inside your Account tab (activeTab === 2) in AccountPage.jsx
function ChangePasswordSection() {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null); // {type: 'success'|'error', text: string}

  const handleSubmit = async () => {
    setMsg(null);
    if (newPw.length < 8)
      return setMsg({
        type: "error",
        text: "New password must be at least 8 characters",
      });
    if (newPw !== confirmPw)
      return setMsg({ type: "error", text: "Passwords don't match" });
    if (newPw === currentPw)
      return setMsg({
        type: "error",
        text: "New password must be different from current",
      });

    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      setMsg({ type: "success", text: "Password updated successfully" });
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.detail || "Failed to update password",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        marginTop: 24,
        padding: "22px 24px",
        borderRadius: 12,
        background: "rgba(255,255,255,0.025)",
        border: `1px solid ${D.border}`,
      }}
    >
      <div
        style={{
          fontFamily: D.fontDisplay,
          fontWeight: 700,
          fontSize: 15,
          color: D.textPrimary,
          marginBottom: 18,
        }}
      >
        Change Password
      </div>

      {msg && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 14px",
            borderRadius: 9,
            background:
              msg.type === "success"
                ? "rgba(52,211,153,0.1)"
                : "rgba(248,113,113,0.1)",
            border: `1px solid ${msg.type === "success" ? "rgba(52,211,153,0.3)" : "rgba(248,113,113,0.3)"}`,
            color: msg.type === "success" ? D.success : D.danger,
            fontSize: 13,
          }}
        >
          {msg.text}
        </div>
      )}

      {[
        ["Current Password", currentPw, setCurrentPw],
        ["New Password", newPw, setNewPw],
        ["Confirm New Password", confirmPw, setConfirmPw],
      ].map(([label, val, setter]) => (
        <div key={label} style={{ marginBottom: 14 }}>
          <div
            style={{
              fontFamily: D.fontMono,
              fontSize: 10.5,
              color: D.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 6,
            }}
          >
            {label}
          </div>
          <input
            type="password"
            value={val}
            onChange={(e) => setter(e.target.value)}
            style={{
              width: "100%",
              padding: "11px 14px",
              borderRadius: 9,
              border: `1px solid ${D.border}`,
              background: "rgba(255,255,255,0.04)",
              color: D.textPrimary,
              fontFamily: D.fontBody,
              fontSize: 14,
              outline: "none",
            }}
            onFocus={(e) => (e.target.style.borderColor = D.accent)}
            onBlur={(e) =>
              (e.target.style.borderColor = "rgba(255,255,255,0.07)")
            }
          />
        </div>
      ))}

      <button
        onClick={handleSubmit}
        disabled={loading || !currentPw || !newPw || !confirmPw}
        style={{
          marginTop: 8,
          padding: "10px 22px",
          borderRadius: 9,
          border: "none",
          background: `linear-gradient(135deg,${D.accent},${D.purple})`,
          color: "white",
          fontFamily: D.fontBody,
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
          opacity: loading || !currentPw || !newPw || !confirmPw ? 0.5 : 1,
        }}
      >
        {loading ? "Updating…" : "Update Password"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function AccountPage() {
  const email = getEmailFromToken();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialTab =
    searchParams.get("tab") === "subscription"
      ? 3
      : searchParams.get("tab") === "usage"
        ? 0
        : searchParams.get("tab") === "topups"
          ? 4
          : 0;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [profile, setProfile] = useState({});
  const [usage, setUsage] = useState({});
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userFlags, setUserFlags] = useState({ hasEbook: false, hasEmailSequences: false, hasVideo: false });
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // ✅ FIX: read email once via ref — prevents useEffect dependency loop
  // const emailRef = useRef(localStorage.getItem("email") || "");
  // const email = emailRef.current;

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const [profResult, usageResult, meResult] = await Promise.allSettled([
          api.get("/auth/brand-profile"),
          api.get("/dashboard/usage"),
          api.get("/auth/me"),
        ]);

        if (cancelled) return;

        if (profResult.status === "fulfilled") {
          setProfile(normaliseProfile(profResult.value.data || {}));
        } else {
          setProfile(normaliseProfile(DEMO_PROFILE));
        }

        if (usageResult.status === "fulfilled") {
          setUsage(usageResult.value.data);
        } else {
          setUsage({
            remaining_trend_searches: 0,
            remaining_content_generations: 0,
            total_trend_searches: 20,
            total_content_generations: 50,
          });
        }

        if (meResult.status === "fulfilled") {
          const me = meResult.value.data || {};
          setUserFlags({
            hasEbook:          !!me.has_ebook,
            hasEmailSequences: !!me.has_email_sequences,
            hasVideo:          !!me.has_video,
          });
        }
      } finally {
        if (!cancelled) {
          setLoadingProfile(false);
          setLoadingUsage(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolveOthers = (draft) => {
    const d = { ...draft };
    // Resolve "Other" custom values and convert multi-select arrays → comma-separated strings
    // (matches the format BrandProfileFormFull uses so MongoDB always stores strings)
    Object.entries(OTHER_FIELDS).forEach(([field, otherKey]) => {
      if (Array.isArray(d[field])) {
        let values = d[field].filter(Boolean);
        if (values.includes("Other")) {
          const custom = (d[otherKey] || "").trim();
          values = custom
            ? values.map((v) => (v === "Other" ? custom : v))
            : values.filter((v) => v !== "Other");
        }
        // Normalize to comma-separated string — same as BrandProfileFormFull formatMultiSelect
        d[field] = values.filter((v) => v !== "Other").join(", ");
      }
      if (otherKey && otherKey in d) delete d[otherKey];
    });
    if (d.platformPreferences) {
      const resolved = {};
      Object.keys(d.platformPreferences).forEach((k) => {
        resolved[k] =
          d.platformPreferences[k] === "Other"
            ? (d.platformPreferencesOther?.[k] || "").trim() || ""
            : d.platformPreferences[k];
      });
      d.platformPreferences = resolved;
    }
    delete d.platformPreferencesOther;
    // Normalize services array → " | " delimited string (same as BrandProfileFormFull's arrayToString)
    if (Array.isArray(d.services)) {
      d.services = d.services.filter((s) => s?.trim()).join(" | ");
    }
    return d;
  };

  const handleSave = async (sectionDraft, logoFile) => {
    setSaving(true);
    try {
      const resolved = resolveOthers(sectionDraft);
      const fd = new FormData();
      fd.append("email", email);
      fd.append("sectionData", JSON.stringify(resolved));
      if (logoFile) fd.append("logo", logoFile);
      const response = await api.patch("/auth/brand-profile/section", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Use server response so the logo (base64 from MongoDB) is reflected immediately
      setProfile(normaliseProfile(response.data || {}));
      setSnack({
        open: true,
        msg: "Changes saved successfully!",
        severity: "success",
      });
    } catch {
      setSnack({
        open: true,
        msg: "Save failed — please try again.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("email");
    localStorage.removeItem("userId");
    navigate("/login");
  };

  const completePct = () => {
    const filled = REQUIRED_KEYS.filter((k) => {
      const v = profile[k];
      return Array.isArray(v) ? v.length > 0 : !!v;
    });
    return Math.round((filled.length / REQUIRED_KEYS.length) * 100);
  };

  const initials = (profile.brandName || email || "?")
    .slice(0, 2)
    .toUpperCase();
  const pct = completePct();

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <style>{G}</style>
      <div className="page-glow" />

      {/* ── Sticky nav ── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 200,
          background: "rgba(17,19,24,0.9)",
          backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${D.border}`,
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 58,
          }}
        >
          {/* Zenpost logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                background: `linear-gradient(135deg,${D.accent},${D.purple})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: D.fontDisplay,
                fontWeight: 800,
                fontSize: 14,
                color: "white",
                animation: "logoGlow 3s ease infinite",
              }}
            >
              Z
            </div>
            <span
              style={{
                fontFamily: D.fontDisplay,
                fontWeight: 700,
                fontSize: 14.5,
                color: D.textPrimary,
              }}
            >
              zenithpost<span style={{ color: D.accent }}>.ai</span>
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={() => navigate("/home")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 13px",
                borderRadius: 9,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${D.border}`,
                color: D.textSec,
                fontFamily: D.fontBody,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              ← Home
            </button>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: `${D.accent}22`,
                border: `2px solid ${D.accentBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                color: D.accent,
                fontFamily: D.fontDisplay,
              }}
            >
              {initials}
            </div>
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: D.textPrimary,
                  lineHeight: 1.1,
                  fontFamily: D.fontDisplay,
                }}
              >
                {profile.brandName || "Your Account"}
              </div>
              <div
                style={{
                  fontSize: 10.5,
                  color: D.textMuted,
                  fontFamily: D.fontMono,
                }}
              >
                {email}
              </div>
            </div>
            <button
              onClick={handleLogout}
              style={{
                padding: "7px 14px",
                borderRadius: 9,
                border: `1px solid rgba(248,113,113,0.3)`,
                background: "rgba(248,113,113,0.07)",
                color: D.danger,
                fontFamily: D.fontBody,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Log out
            </button>
          </div>
        </div>
      </div>

      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "36px 24px 80px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header */}
        <div className="fade-up" style={{ marginBottom: 32 }}>
          <div
            style={{
              fontFamily: D.fontMono,
              fontSize: 10.5,
              color: D.textMuted,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            My Account
          </div>
          <Typography
            sx={{
              fontFamily: D.fontDisplay,
              fontWeight: 800,
              fontSize: { xs: "24px", md: "32px" },
              color: D.textPrimary,
              lineHeight: 1.15,
              mb: 1.25,
            }}
          >
            Account & Brand Profile
          </Typography>
          <Typography
            sx={{
              fontSize: 14.5,
              color: D.textSec,
              maxWidth: 480,
              lineHeight: 1.7,
            }}
          >
            Monitor your usage and keep your brand intelligence up to date.
          </Typography>
        </div>

        {/* Tabs */}
        <div className="fade-up fade-up-1" style={{ marginBottom: 28 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: `1px solid ${D.border}` }}
          >
            <Tab
              icon={
                <AnalyticsOutlinedIcon sx={{ fontSize: "16px !important" }} />
              }
              iconPosition="start"
              label="Usage & Stats"
            />
            <Tab
              icon={
                <BusinessCenterOutlinedIcon
                  sx={{ fontSize: "16px !important" }}
                />
              }
              iconPosition="start"
              label="Brand Profile"
            />
            <Tab
              icon={<PersonOutlineIcon sx={{ fontSize: "16px !important" }} />}
              iconPosition="start"
              label="Account"
            />
            <Tab label="Subscription" />
            <Tab label="Top-ups" />
          </Tabs>
        </div>

        {/* TAB 0: USAGE */}
        {activeTab === 0 && (
          <div className="fade-up">
            <UsageTab usage={usage} loading={loadingUsage} userFlags={userFlags} />
          </div>
        )}

        {/* TAB 1: BRAND PROFILE */}
        {activeTab === 1 && (
          <div className="fade-up">
            {loadingProfile ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="sk" style={{ height: 74 }} />
                ))}
              </div>
            ) : (
              <>
                <div
                  style={{
                    background: D.surface,
                    border: `1px solid ${D.border}`,
                    borderRadius: 12,
                    padding: "14px 18px",
                    marginBottom: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: D.textPrimary,
                        }}
                      >
                        Profile Completeness
                      </span>
                      <span
                        style={{
                          fontFamily: D.fontMono,
                          fontSize: 12,
                          fontWeight: 700,
                          color: pct >= 90 ? D.success : D.accent,
                        }}
                      >
                        {pct}%
                      </span>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        "& .MuiLinearProgress-bar": {
                          background: pct >= 90 ? D.success : D.accent,
                        },
                      }}
                    />
                  </div>
                  {pct >= 90 && (
                    <CheckCircleOutlineIcon
                      sx={{
                        color: D.success,
                        fontSize: "22px !important",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </div>
                {PROFILE_SECTIONS.map((s) => (
                  <ProfileSection
                    key={s.id}
                    section={s}
                    profile={profile}
                    onSave={handleSave}
                    saving={saving}
                  />
                ))}
              </>
            )}
          </div>
        )}

        {/* TAB 2: ACCOUNT */}
        {/* TAB 2: ACCOUNT */}
        {activeTab === 2 && (
          <div className="fade-up">
            <div
              style={{
                background: D.surface,
                borderRadius: 14,
                border: `1px solid ${D.border}`,
                overflow: "hidden",
              }}
            >
              {/* Profile header — unchanged */}
              <div
                style={{
                  padding: "28px 28px 22px",
                  borderBottom: `1px solid ${D.border}`,
                  background: "rgba(108,126,248,0.03)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: `${D.accent}20`,
                      border: `3px solid ${D.accentBorder}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 24,
                      fontWeight: 800,
                      color: D.textPrimary,
                      fontFamily: D.fontDisplay,
                      flexShrink: 0,
                    }}
                  >
                    {initials}
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: D.fontDisplay,
                        fontWeight: 800,
                        fontSize: 20,
                        color: D.textPrimary,
                        lineHeight: 1.1,
                      }}
                    >
                      {profile.brandName || "Your Brand"}
                    </div>
                    <div
                      style={{
                        fontFamily: D.fontMono,
                        fontSize: 11.5,
                        color: D.textSec,
                        margin: "5px 0 10px",
                      }}
                    >
                      {email}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {(profile.industries || []).slice(0, 5).map((i) => (
                        <Chip
                          key={i}
                          label={i}
                          size="small"
                          sx={{
                            background: `${D.accent}12 !important`,
                            color: `${D.accent} !important`,
                            border: `1px solid ${D.accentBorder} !important`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Account details */}
              <div style={{ padding: "22px 28px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))",
                    gap: 12,
                    marginBottom: 22,
                  }}
                >
                  {[
                    ["✉️", "Email", email],
                    ["🌐", "Website", profile.companyWebsite || "—"],
                    ["🌍", "Language", profile.contentLanguage || "English"],
                  ].map(([ico, k, v]) => (
                    <div
                      key={k}
                      style={{
                        padding: "14px 16px",
                        borderRadius: 10,
                        background: "rgba(255,255,255,0.025)",
                        border: `1px solid ${D.border}`,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: D.fontMono,
                          fontSize: 10.5,
                          color: D.textMuted,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          marginBottom: 5,
                        }}
                      >
                        {ico} {k}
                      </div>
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: D.textPrimary,
                          wordBreak: "break-all",
                        }}
                      >
                        {v}
                      </div>
                    </div>
                  ))}
                </div>

                <Divider sx={{ mb: 2.5 }} />

                {/* ✅ ChangePasswordSection now rendered here — fully functional */}
                <ChangePasswordSection />
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: SUBSCRIPTION */}
        {activeTab === 3 && (
          <div className="fade-up">
            <SubscriptionTab email={email} />
          </div>
        )}

        {/* TAB 4: TOP-UPS */}
        {activeTab === 4 && (
          <div className="fade-up">
            <TopupsTab email={email} userFlags={userFlags} />
          </div>
        )}
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{
            borderRadius: "10px",
            fontFamily: D.fontBody,
            fontWeight: 600,
            background:
              snack.severity === "success"
                ? "rgba(52,211,153,0.12)"
                : "rgba(248,113,113,0.12)",
            color: snack.severity === "success" ? D.success : D.danger,
            border: `1px solid ${snack.severity === "success" ? "rgba(52,211,153,0.3)" : "rgba(248,113,113,0.3)"}`,
          }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
